import { useEffect, useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import Spinner from "../Components/Spinner";
import ErrorMessage from "../Components/ErrorMessage";
import { LuUserCheck, LuUserCog, LuUserPen, LuUserRound } from "react-icons/lu";
import { LiaUserShieldSolid } from "react-icons/lia";
import { CiCircleAlert, CiCircleCheck } from "react-icons/ci";
import { FaCheck, FaRegCircle, FaTimes } from "react-icons/fa";
import { BiCheck, BiPlus } from "react-icons/bi";
import { MdOutlineCheckCircle, MdOutlineCircle } from "react-icons/md";
import RoleModal from "./RoleModal";
import NewOrganizationModal from "../Organizations/NewOrganizationModal";
import { useLanguage } from "../Translations/LenguageContext";
import MandatorySign from "../Components/MandatorySign";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;



const roles = [
    { value: "manager", label: "Manager" },
    { value: "developer", label: "Developer" },
    { value: "member", label: "Member" }
];

const NewMemberModal = ({ account_id, show, handleClose, fetchRecords, userData, orgData, fetchOrgData, org_id = null }) => {
    const {t} = useLanguage()
    const [loadingSave, setLoadingSave] = useState(false);
    const [errorMessage, setErrorMessage] = useState({ message: "", display: false });
    const [openRoleModal, setOpenRoleModal] = useState(false)
    const [openOrgModal, setOpenOrgModal] = useState(false)
    const [formData, setFormData] = useState({
        email: "",
        role: "",
        organization_ids: []

    });

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRoleSelect = (role) => {
        setFormData(prev => ({
            ...prev,
            role
        }));
    };

    const handleOrgSelect = (orgId) => {
        setFormData(prev => {
            const alreadySelected = prev.organization_ids.includes(orgId);

            return {
                ...prev,
                organization_ids: alreadySelected
                    ? prev.organization_ids.filter(id => id !== orgId)
                    : [...prev.organization_ids, orgId]
            };
        });
    };

    const removeOrganization = (orgId) => {
        setFormData(prev => ({
            ...prev,
            organization_ids: prev.organization_ids.filter(id => id !== orgId)
        }));
    };

    const handleSave = async () => {
        if (userData.email === formData.email) {
            setErrorMessage({
                message: "You cannot send an invitation to your own email address.",
                display: true,
            });
            return;
        }

        if (!formData.email.trim()) {
            setErrorMessage({ message: "Email is required", display: true });
        }

        if (!formData.role) {
            setErrorMessage({ message: "Role is required", display: true });
            return;
        }


        if (formData.organization_ids.length == 0) {
            setErrorMessage({ message: "Organization is required", display: true });
            return;
        }

        setLoadingSave(true);
        setErrorMessage({ message: "", display: false });

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/send_invitation`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id: account_id,
                    email: formData.email,
                    role: formData.role,
                    organization_ids: formData.organization_ids
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Something went wrong");
            }

            if (fetchRecords) {
                await fetchRecords();
            }

            closeModal();
        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true
            });
        } finally {
            setLoadingSave(false);
        }
    };

    const closeModal = () => {
        handleClose();
        setFormData({ email: "", role: "", organization_ids: [] });
        setErrorMessage({ message: "", display: false });
        setLoadingSave(false);
    };

    useEffect(() => {
        fetchOrgData();
    }, [account_id]);


    useEffect(() => {
        if (org_id) {
            setFormData({
                organization_ids: [org_id]
            })
        }
    }, [show, org_id])


    return (
        <Modal show={show} centered onHide={closeModal}>
            <div className="modal_header">
                <p className="modal_header_title">{t.members.invite_member}</p>
            </div>

            <div className="modal_main_cont">
                <div className="modal_profile_input_cont">
                    {!org_id && (
                        <div className="label_input">
                            <label className="input_label">{t.sidebar.organization} <MandatorySign message={t.mandatory_field}/></label>
                            <Dropdown>
                                <Dropdown.Toggle
                                    as="div"
                                    className="register_form_first fade-in"
                                >
                                    {
                                        formData.organization_ids.length > 0
                                            ? `${formData.organization_ids.length} ${t.selected}`
                                            : t.select
                                    }
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="custom_dropdown_menu">
                                    <div className="dropdown_container fade-in">
                                        {orgData && orgData.map((org) => (
                                            (org.role == "owner" || org.role == "manager") && (
                                                <button
                                                    key={org.id}
                                                    onClick={() => handleOrgSelect(org.id)}
                                                    className="settings_link"
                                                >
                                                    <i >
                                                        {formData.organization_ids.includes(org.id)
                                                            ? <MdOutlineCheckCircle />
                                                            : <MdOutlineCircle />}
                                                    </i>
                                                    {org.name}
                                                </button>
                                            )
                                        ))}
                                        <button
                                            onClick={() => setOpenOrgModal(true)}
                                            className="settings_link"
                                        >
                                            <i><BiPlus /></i> {t.create_new}
                                        </button>
                                    </div>
                                </Dropdown.Menu>

                            </Dropdown>
                            <div className="selected_items_container">
                                {formData.organization_ids.map(id => {
                                    const org = orgData.find(o => o.id === id);
                                    if (!org) return null;

                                    return (
                                        <span key={id} className="selected_item_chip">
                                            {org.name}
                                            <i
                                                className="remove_item_icon"
                                                onClick={() => removeOrganization(id)}
                                            >
                                                <FaTimes />
                                            </i>
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="label_input">
                        <label className="input_label">{t.members.role} <MandatorySign message={t.mandatory_field}/></label>
                        <Dropdown autoClose={true}>
                            <Dropdown.Toggle
                                as="div"
                                className="register_form_first fade-in"
                            >
                                {
                                    formData.role
                                        ? formData.role.charAt(0).toUpperCase() + formData.role.slice(1)
                                        : t.select
                                }
                            </Dropdown.Toggle>

                            <Dropdown.Menu className="custom_dropdown_menu">
                                <div className="dropdown_container fade-in">
                                    {roles.map((role) => (
                                        <Dropdown.Item
                                            key={role.value}
                                            onClick={() => handleRoleSelect(role.value)}
                                            className={`settings_link ${role.value == formData.role ? "settings_link_selected" : ""}`}
                                        >
                                            {/* {role.value === "owner" && <i><LuUserCheck /></i>} */}
                                            {role.value === "manager" && <i><LuUserCog /></i>}
                                            {role.value === "developer" && <i><LuUserPen /></i>}
                                            {role.value === "member" && <i><LuUserRound /></i>}
                                            {" "}
                                            {role.label}
                                        </Dropdown.Item>
                                    ))}
                                </div>
                            </Dropdown.Menu>
                        </Dropdown>
                        {formData.role && (
                            <p className="role_warning_text">
                                <i><CiCircleAlert /></i>{" "}
                                {t.members.role_options[formData.role] || t.members.role_options.member}
                                {t.members.more_info} <span onClick={() => setOpenRoleModal(true)}>{t.here}</span>
                            </p>
                        )}
                    </div>

                    <div className="label_input">
                        <label className="input_label">Email <MandatorySign message={t.mandatory_field}/></label>
                        <input
                            type="text"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="user@example.com"
                            className="register_form_first fade-in"
                            autoComplete="off"
                        />
                    </div>

                </div>
            </div>

            <div className="modal_footer">
                <div className="modal_footer_err_cont">
                    <ErrorMessage err={errorMessage} />
                </div>

                <div className="modal_footer_btn_cont">
                    <button
                        className="modale_save_btn"
                        onClick={handleSave}
                        disabled={loadingSave}
                    >
                        {loadingSave ? <Spinner loading={true} /> : t.members.invite}
                    </button>

                    <button
                        className="modale_close_btn"
                        onClick={closeModal}
                        disabled={loadingSave}
                    >
                        {t.close}
                    </button>
                </div>
            </div>

            <RoleModal
                show={openRoleModal}
                handleClose={() => setOpenRoleModal(false)}
            />

            <NewOrganizationModal
                account_id={account_id}
                show={openOrgModal}
                handleClose={() => setOpenOrgModal(false)}
                fetchRecords={fetchOrgData}
                userData={userData}
            />
        </Modal>
    );
};

export default NewMemberModal;